import { Injectable } from '@angular/core';

/**
 * Several logged-in accounts on one POS browser ("Switch Company"). Each account's session keys
 * (token, store, counter shift state) are kept as a snapshot; switching swaps the snapshot into
 * localStorage and reloads, so every root service starts from the new account. Switching does
 * not call the logout API, so the previous account's counter session stays open on the server.
 */

const REGISTRY_KEY = 'elixr_pos_accounts';

/** localStorage keys that belong to the logged-in account */
const SESSION_KEYS = [
  'name', 'storename', 'client_id', 'pos_industry', 'auth_credentials', 'auth_token', 'user_data',
  'punchStatus', 'shiftStartTime', 'shiftEndTime', 'client_sale_data', 'msmeno', 'msmetype',
];

/** Per-sale scratch state that must not follow the cashier into another company */
const CLEARED_ON_SWITCH = ['mobile_variant_stock_reservations'];

/** The POS has no login route: the dashboard shows its login dialog when nobody is signed in */
const LOGIN_PATH = '/dashboard';

/** Browser-level preferences kept when the app clears storage */
const BROWSER_KEYS = [REGISTRY_KEY, 'pos-theme'];

export interface SavedPosAccount {
  /** client_id:user_id */
  id: string;
  clientName: string;
  storeName: string;
  userName: string;
  username: string;
  tokenExp: number | null;
  snapshot: Record<string, string>;
}

interface Registry {
  version: 1;
  activeId: string | null;
  accounts: SavedPosAccount[];
}

export interface PosAccountOption extends Omit<SavedPosAccount, 'snapshot'> {
  active: boolean;
  expired: boolean;
}

@Injectable({ providedIn: 'root' })
export class AccountRegistryService {
  /** Set by the billing screen so a switch can warn about an unsaved bill */
  private pendingBillItems: () => number = () => 0;

  constructor() {
    window.addEventListener('storage', (event) => {
      if (event.key !== REGISTRY_KEY) return;
      const before = this.parse(event.oldValue)?.activeId ?? null;
      const after = this.parse(event.newValue)?.activeId ?? null;
      if (before !== after) window.location.reload();
    });
  }

  setPendingBillCounter(counter: (() => number) | null): void {
    this.pendingBillItems = counter ?? (() => 0);
  }

  pendingItems(): number {
    return this.pendingBillItems();
  }

  list(): PosAccountOption[] {
    const registry = this.read();
    const now = Date.now() / 1000;
    return registry.accounts
      .map(({ snapshot, ...account }) => ({
        ...account,
        active: account.id === registry.activeId,
        expired: !!account.tokenExp && account.tokenExp < now,
      }))
      .sort((a, b) => Number(b.active) - Number(a.active));
  }

  /** After login / SSO login: remember this account (replacing an older entry) and make it active */
  registerActive(username = ''): void {
    const registry = this.read();
    const id = this.currentId();
    const entry = this.captureCurrent(username || this.find(registry, id)?.username || '');
    if (!entry) return;
    registry.accounts = [...registry.accounts.filter((a) => a.id !== entry.id), entry];
    registry.activeId = entry.id;
    this.write(registry);
  }

  /** Saves the current session, clears it and shows login for another account */
  beginAddAccount(): void {
    this.saveActiveSnapshot();
    this.clearSessionKeys();
    const registry = this.read();
    registry.activeId = null;
    this.write(registry);
    window.location.assign(LOGIN_PATH);
  }

  switchTo(id: string): void {
    this.saveActiveSnapshot();
    if (this.restore(id)) {
      window.location.assign('/dashboard');
    } else {
      const target = this.find(this.read(), id);
      window.location.assign(target ? `${LOGIN_PATH}?username=${encodeURIComponent(target.username)}` : LOGIN_PATH);
    }
  }

  /**
   * Puts a saved account's keys back into localStorage. Returns false (and leaves the browser
   * signed out) when the account is unknown or its token has expired.
   */
  restore(id: string): boolean {
    const registry = this.read();
    const target = this.find(registry, id);
    this.clearSessionKeys();
    CLEARED_ON_SWITCH.forEach((key) => localStorage.removeItem(key));
    Object.keys(localStorage).filter((key) => key.startsWith('receipt_')).forEach((key) => localStorage.removeItem(key));
    if (!target || (target.tokenExp && target.tokenExp < Date.now() / 1000)) {
      registry.activeId = null;
      this.write(registry);
      return false;
    }
    Object.entries(target.snapshot).forEach(([key, value]) => localStorage.setItem(key, value));
    registry.activeId = target.id;
    this.write(registry);
    return true;
  }

  /** Signing out of the active account: forget it and return the next usable one, if any */
  removeActive(): SavedPosAccount | null {
    const registry = this.read();
    const activeId = registry.activeId ?? this.currentId();
    registry.accounts = registry.accounts.filter((a) => a.id !== activeId);
    registry.activeId = null;
    this.write(registry);
    return registry.accounts.find((a) => !(a.tokenExp && a.tokenExp < Date.now() / 1000)) ?? null;
  }

  markActiveExpired(): void {
    const registry = this.read();
    const entry = this.find(registry, registry.activeId);
    if (!entry) return;
    entry.tokenExp = 1;
    this.write(registry);
  }

  /** localStorage.clear() for this app, keeping the saved accounts and browser preferences */
  clearStorageKeepingAccounts(): void {
    const kept = BROWSER_KEYS.map((key) => [key, localStorage.getItem(key)] as const);
    localStorage.clear();
    kept.forEach(([key, value]) => { if (value !== null) localStorage.setItem(key, value); });
  }

  private saveActiveSnapshot(): void {
    if (localStorage.getItem('name')) this.registerActive();
  }

  private captureCurrent(username: string): SavedPosAccount | null {
    const user = this.parse(localStorage.getItem('name'));
    if (!user?.accessToken) return null;
    const store = this.parse(localStorage.getItem('storename'));
    const sale = this.parse(localStorage.getItem('client_sale_data'));
    const snapshot: Record<string, string> = {};
    SESSION_KEYS.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value !== null) snapshot[key] = value;
    });
    return {
      id: this.currentId()!,
      clientName: sale?.client_name || sale?.clientname || user.client_name || store?.name || 'Company',
      storeName: store?.name || '',
      userName: user.name || username,
      username,
      tokenExp: this.tokenExp(user.accessToken),
      snapshot,
    };
  }

  private currentId(): string | null {
    const user = this.parse(localStorage.getItem('name'));
    return user ? `${user.client_id ?? 0}:${user.id ?? user.uuid ?? ''}` : null;
  }

  private clearSessionKeys(): void {
    SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
  }

  private tokenExp(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      return typeof payload.exp === 'number' ? payload.exp : null;
    } catch {
      return null;
    }
  }

  private find(registry: Registry, id: string | null): SavedPosAccount | undefined {
    return id ? registry.accounts.find((a) => a.id === id) : undefined;
  }

  private read(): Registry {
    const registry = this.parse(localStorage.getItem(REGISTRY_KEY));
    return registry?.version === 1 && Array.isArray(registry.accounts)
      ? registry
      : { version: 1, activeId: null, accounts: [] };
  }

  private write(registry: Registry): void {
    localStorage.setItem(REGISTRY_KEY, JSON.stringify(registry));
  }

  private parse(value: string | null): any {
    try {
      return value ? JSON.parse(value) : null;
    } catch {
      return null;
    }
  }
}
